<template>
    <div
        class="ticket notranslate"
    >
        <div class="ticket-title">
            <div class="ticket-title-group">
                <span v-if="isLive" class="ticket-livetag">
                    {{ getTranslationBySite('live') }}
                </span>
                <SvgIcon v-bind:iconName="iconName" />
                <span class="ticket-league" v-bind:class="{ _live: isLive }">
                    {{ leagueName }}
                </span>
            </div>
            <div
                class="ticket-close JARVIS_BeTticket_Btn_DeleteTicket"
                v-on:click="removeTicket"
            >
                <SvgIcon iconName="systemIcon_close" />
            </div>
        </div>
        <div class="ticket-odd">
            <div class="ticket-match">
                <span v-if="isLive && betSlip.selection.score" class="ticket-live-score">
                    [{{ betSlip.selection.score.home }}:{{ betSlip.selection.score.away }}]
                </span>
                <span class="ticket-team">
                    {{ homeTeamName }}
                </span>
                -
                <span class="ticket-team">
                    {{ awayTeamName }}
                </span>
            </div>
            <div class="ticket-odd-content">
                <div class="ticket-odd-info">
                    <div class="ticket-marketType">
                        {{ betSlip.market.name }}
                    </div>
                    <div class="ticket-oddType">
                        {{ betSlip.selection.name }}
                    </div>
                </div>
                <div class="ticket-odd-main">
                    <div class="ticket-oddValue" v-bind:class="{ _negative: isOddsNegative }">
                        {{ displayPrice }}
                    </div>
                </div>
                <div
                    class="ticket-refresh-btn"
                    v-bind:class="{ _refreshing: isRefreshing }"
                    v-on:click="refresh(false)"
                >
                    <SvgIcon iconName="systemIcon_refresh_centered" style="fill: #000000; font-size: 1.8rem;" />
                </div>
            </div>
        </div>
    </div>
</template>

<script setup lang="ts">
    import { storeToRefs } from 'pinia';
    import { computed, onMounted, onUnmounted, ref } from 'vue';
    import { SportType } from '@/interface/enum';
    import { hasLastStake } from '@/libraries/betSlip/lastStakeStorage';
    import { getTranslationBySite } from '@/libraries/helpers/i18nHelper';
    import { isScoreChanged } from '@/libraries/helpers/scoreHelper';
    import { getTranslated } from '@/libraries/helpers/translationHelper';
    import { i18n } from '@/libraries/i18n';
    import { useSelectionSubscriptionCallback } from '@/libraries/oddsApi/composables/useSelectionSubscriptionCallback';
    import { useSimpleEventSubscriptionCallback } from '@/libraries/oddsApi/composables/useSimpleEventSubscriptionCallback';
    import { invertCapitalize } from '@/libraries/string';
    import { formatMinDecimalPlace } from '@/libraries/utils/text';
    import { useBetSlipStore } from '@/stores/betSlip/betSlipStore';
    import { useDynamicMarketBetSlipStore } from '@/stores/betSlip/dynamicMarketBetSlipStore';
    import { useRootStore } from '@/stores/rootStore';
    import SvgIcon from '@/svg/SvgIcon.vue';

    const { betSlip, isLive } = storeToRefs(useDynamicMarketBetSlipStore());
    const { removeTicket, openTicket, setBetSlipMessage } = useDynamicMarketBetSlipStore();

    const iconName = computed(() => `sportIcon_${invertCapitalize(SportType[betSlip.value?.match?.sportType])}`);

    const displayPrice = computed(() => (betSlip.value.selection.price?.value ? formatMinDecimalPlace(betSlip.value.selection.price.value, 2) : ''));
    const isOddsNegative = computed(() => parseFloat(displayPrice.value) < 0);

    const leagueName = computed(() => getTranslated(betSlip.value.match.leagueName));
    const homeTeamName = computed(() => getTranslated(betSlip.value.match.homeTeamName));
    const awayTeamName = computed(() => getTranslated(betSlip.value.match.awayTeamName));

    function hasShowStakePad() {
        const { setIsShowStakePad } = useBetSlipStore();

        const hasDefaultStake = hasLastStake();
        if (!hasDefaultStake) {
            setIsShowStakePad(false);
        }
    }

    const ticks = ref(0);
    const isRefreshing = ref(false);

    function tick() {
        ticks.value++;
        if (ticks.value >= 10) {
            fakeRefresh();
        }
    }

    async function refresh(isFirstTime: boolean) {
        ticks.value = 0;
        refreshAnimation();
        await openTicket(isFirstTime);
    }

    function fakeRefresh() {
        refreshAnimation();
    }

    function refreshAnimation() {
        isRefreshing.value = true;
        setTimeout(() => {
            isRefreshing.value = false;
        }, 1000);
    }

    const { onUpdated, onDeleted } = useSelectionSubscriptionCallback({
        marketId: ref(betSlip.value.market.id),
        selectionId: ref(betSlip.value.selection.id),
        sportType: betSlip.value.match.sportType,
        leagueSize: betSlip.value.match.leagueSize,
        leagueId: betSlip.value.match.leagueId,
    });

    const { isMixParlayMode } = storeToRefs(useRootStore());
    const { onSubscription: onEventSubscription } = useSimpleEventSubscriptionCallback({
        eventId: ref(betSlip.value.match.matchId),
        isMixParlay: isMixParlayMode,
    });

    const { setTopScreenToast } = useRootStore();
    onEventSubscription(async (event) => {
        if (!event) {
            setTopScreenToast({
                status: 'error',
                messageKey: 'betSlip_errorMessage_eventClosed',
            });
            await removeTicket();
            return;
        }

        const oldScore = {
            home: betSlip.value.match.liveHomeScore,
            away: betSlip.value.match.liveAwayScore,
        };
        const newScore = {
            home: event.mainMarketEventResult.liveHomeScore,
            away: event.mainMarketEventResult.liveAwayScore,
        };
        if (isScoreChanged(oldScore, newScore)) {
            setBetSlipMessage(i18n.t('betSlip_errorMessage_scoreChanged', {
                oldScore: `${oldScore.home}:${oldScore.away}`,
                newScore: `${newScore.home}:${newScore.away}`,
            }));
            await refresh(false);
        }
    });

    onUpdated(async () => {
        await refresh(false);
    });

    onDeleted(() => {
        removeTicket();
    });

    onMounted(() => {
        hasShowStakePad();
        window.eventEmitter.on('tick', tick);
    });

    onUnmounted(() => {
        window.eventEmitter.off('tick');
    });
</script>

<style lang="scss" scoped>
    @import "@/scss/components/ticket";
</style>
